import React, { useEffect, useState, useMemo } from 'react';
import NorthIcon from '@mui/icons-material/North';
import SouthIcon from '@mui/icons-material/South';
import { Box, Typography } from '@mui/material';
import { getDashboardVisaoGeralVOP } from '../../../../api/dashboard';

const useVisaoGeralVopHook = ({ filtroDashboard, loadingDashFim, setAlerta }) => {
  const [isPending, setIsPending] = useState(false);
  const [data, setData] = useState({ totais: {}, meses: {} });
  const [anos, setAnos] = useState([]);
  const [anoSelecionado, setAnoSelecionado] = useState(null);

  const moedaFormatter = useMemo(
    () => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }),
    []
  );
  const pctFormatter = useMemo(
    () => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    []
  );

  const formatarMoeda = value => moedaFormatter.format(value);
  const formatarPorcentagem = value => `${pctFormatter.format(value)}%`;
  const corValor = value => (value >= 100 ? 'texto-success-strong' : 'texto-danger-strong-2');
  const iconeValor = value =>
    value > 100 ? (
      <NorthIcon fontSize="small" className="texto-success-strong margin-right-05" />
    ) : value < 100 ? (
      <SouthIcon fontSize="small" className="texto-danger-strong-2 margin-right-05" />
    ) : null;

  useEffect(() => {
    carregarDashBoard({ ignoreAno: true });
  }, [filtroDashboard]);

  const carregarDashBoard = async (opts = {}) => {
    try {
      setIsPending(true);
      const params = { ...filtroDashboard };
      if (!opts.ignoreAno && anoSelecionado) {
        params.ano = anoSelecionado;
      }
      const res = await getDashboardVisaoGeralVOP(params);
      if (!res.data.success) {
        setAlerta({
          mensagem:
            res.data.mensagem || 'Ocorreu um erro ao buscar dados de produto. Favor contatar o suporte.',
          tipo: 'error'
        });
        return;
      }

      const { anos: anosRetornados, totais, meses } = res.data.data;
      if (
        Array.isArray(anosRetornados) &&
        anosRetornados.length > 0 &&
        (opts.ignoreAno || !anoSelecionado)
      ) {
        setAnos(anosRetornados);
        setAnoSelecionado(anosRetornados[0]);
      }
      setData({ totais, meses });
    } catch (err) {
      setAlerta({
        mensagem:
          'Houve um erro ao buscar os dados do Dashboard "Visão Geral | Pipe Comercial x VOP x À Realizar".',
        tipo: 'error'
      });
    } finally {
      setIsPending(false);
      loadingDashFim('VisaoGeralVop');
    }
  };

  const columns = useMemo(
    () => [
      {
        field: 'mes',
        headerName: 'Mês',
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        headerClassName: 'texto-semi-negrito',
        cellClassName: 'texto-semi-negrito'
      },
      {
        field: 'valorPipe',
        headerName: 'Pipe Comercial (R$)',
        flex: 1,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
        headerClassName: 'texto-semi-negrito',
        valueFormatter: ({ value }) => formatarMoeda(value)
      },
      {
        field: 'valorVOP',
        headerName: 'VOP (R$)',
        flex: 1,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
        headerClassName: 'texto-semi-negrito',
        valueFormatter: ({ value }) => formatarMoeda(value)
      },
      {
        field: 'aRealizar',
        headerName: 'À Realizar (R$)',
        flex: 1,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
        headerClassName: 'texto-semi-negrito',
        valueFormatter: ({ value }) => formatarMoeda(value)
      },
      {
        field: 'atingimento',
        headerName: '% Atingimento',
        flex: 1,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
        headerClassName: 'texto-semi-negrito',
        valueFormatter: ({ value }) => formatarPorcentagem(value),
        renderCell: params => {
          const val = params.value;
          return (
            <Box className={corValor(val) + ' inline-flex items-centro-verticalmente'}>
              {iconeValor(val)}
              <Typography>{formatarPorcentagem(val)}</Typography>
            </Box>
          );
        }
      }
    ],
    [formatarMoeda, formatarPorcentagem, corValor, iconeValor]
  );

  const rows = useMemo(
    () =>
      Object.entries(data.meses).map(([key, m]) => ({
        id: Number(key),
        mes: m.mes,
        valorPipe: m.valorPipeComercial,
        valorVOP: m.valorVOP,
        aRealizar: m.aRealizar,
        atingimento: m.atingimento
      })),
    [data.meses]
  );

  const chartData = useMemo(() => {
    const header = ['Mês', 'Pipe Comercial (R$)', 'VOP (R$)', 'À Realizar', 'À Realizar (borda)'];
    const linhas = Object.values(data.meses).map(m => [
      m.mes,
      m.valorPipeComercial,
      m.valorVOP,
      m.aRealizar,
      m.aRealizar
    ]);
    return [header, ...linhas];
  }, [data.meses]);

  const chartOptions = useMemo(
    () => ({
      width: '100%',
      height: 400,
      chartArea: { width: '80%', height: '70%' },
      seriesType: 'bars',
      colors: ['#1A284D', '#5AD4FF'],
      series: {
        2: {
          type: 'line',
          color: '#B3261E',
          lineWidth: 2,
          pointSize: 10
        },
        3: {
          type: 'line',
          color: '#fff',
          pointShape: 'circle',
          pointSize: 5,
          lineWidth: 0,
          visibleInLegend: false,
          enableInteractivity: false
        }
      },
      legend: { position: 'bottom' },
      animation: { duration: 500, easing: 'inAndOut' }
    }),
    []
  );

  return {
    isPending,
    data,
    anos,
    anoSelecionado,
    setAnoSelecionado,
    carregarDashBoard,
    formatarMoeda,
    formatarPorcentagem,
    corValor,
    iconeValor,
    columns,
    rows,
    chartData,
    chartOptions
  };
};

export default useVisaoGeralVopHook;
